/**
 * Public client portal: read-only follow-up of project reservations (SAV).
 * Accessed via a tokenized URL (/client/reserves/:token), no authentication.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, WarningCircle, Eye, MapPin, Camera, Lock, Buildings } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  en_cours: { label: 'En cours', bg: '#FFF3E0', color: '#EF6C00', icon: Clock },
  fait: { label: 'Levee', bg: '#E8F5E9', color: '#2E7D32', icon: CheckCircle },
  pas_fait: { label: 'Bloquee', bg: '#FFEBEE', color: '#C62828', icon: WarningCircle },
};

const ClientReservationsPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/client-reservations/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 410) setError('Ce lien a expire. Demandez un nouveau lien a votre interlocuteur BATI-BOYS.');
          else if (e.response?.status === 404) setError('Lien invalide ou revoque.');
          else setError('Erreur de chargement. Verifiez votre connexion.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', fontSize: '0.92rem', color: '#5E5F66' }}>Chargement…</div>;
  }
  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: 'center', background: 'white', padding: '28px 24px', borderRadius: 12, border: '1px solid #FECACA' }}>
          <WarningCircle size={48} color="#C62828" />
          <h2 style={{ marginTop: 12, fontSize: '1.1rem' }}>{error || 'Lien inaccessible'}</h2>
        </div>
      </div>
    );
  }

  const items = data.items.filter((i) => filter === 'all' || i.status === filter);
  const p = data.project;
  const s = data.stats;

  return (
    <div data-testid="client-reservations-portal" style={{ minHeight: '100vh', background: '#F4F5F7', paddingBottom: 32 }}>
      <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 100, padding: '6px 14px', background: 'linear-gradient(135deg, #60A5FA, #2563EB)', color: 'white', borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Eye size={12} weight="bold" /> LECTURE SEULE
      </div>

      <header style={{ background: 'linear-gradient(135deg, #002FA7, #1565C0)', color: 'white', padding: '24px 28px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ fontSize: '0.78rem', opacity: 0.9, letterSpacing: 1 }}>{p.company.toUpperCase()} - SUIVI DES RESERVES</div>
          <h1 style={{ margin: '4px 0 8px', fontSize: '1.6rem' }}>{p.name}</h1>
          <div style={{ display: 'flex', gap: 16, fontSize: '0.86rem', flexWrap: 'wrap' }}>
            {p.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {p.address}</span>}
            {p.client_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Buildings size={14} /> {p.client_name}</span>}
          </div>
          <div style={{ marginTop: 16, background: 'rgba(255,255,255,0.18)', borderRadius: 999, height: 10, overflow: 'hidden', maxWidth: 320 }}>
            <div style={{ width: `${s.progress_pct}%`, height: '100%', background: '#FCD34D', transition: 'width 0.4s' }} />
          </div>
          <div style={{ marginTop: 4, fontSize: '0.78rem' }}>
            <strong>{s.done} / {s.total}</strong> reserves levees ({s.progress_pct}%)
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '20px 28px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: `Toutes (${s.total})`, color: '#5E5F66' },
            { id: 'en_cours', label: `En cours (${s.pending})`, color: '#EF6C00' },
            { id: 'fait', label: `Levees (${s.done})`, color: '#2E7D32' },
            { id: 'pas_fait', label: `Bloquees (${s.blocked})`, color: '#C62828' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`portal-filter-${f.id}`}
              style={{
                padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontSize: '0.82rem', fontWeight: 700,
                background: filter === f.id ? f.color : '#E2E2E6',
                color: filter === f.id ? 'white' : '#5E5F66',
              }}
            >{f.label}</button>
          ))}
        </div>

        {items.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#5E5F66', background: 'white', borderRadius: 10 }}>Aucune reserve dans cette categorie</div>
        ) : (
          items.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.en_cours;
            const Icon = meta.icon;
            return (
              <div key={r.id} data-testid={`portal-item-${r.id}`} style={{ padding: '14px 18px', background: 'white', borderRadius: 10, marginBottom: 10, borderLeft: `4px solid ${meta.color}`, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: '1rem' }}>{r.title}</strong>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 700, background: meta.bg, color: meta.color }}>
                      <Icon size={12} weight="bold" /> {meta.label}
                    </span>
                  </div>
                  {r.description && <div style={{ fontSize: '0.86rem', color: '#1E2030', marginBottom: 6 }}>{r.description}</div>}
                  <div style={{ fontSize: '0.78rem', color: '#5E5F66', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {r.location && <span><MapPin size={12} style={{ verticalAlign: 'middle' }} /> {r.location}</span>}
                    {r.opened_at && <span>Ouverte le {r.opened_at.split('-').reverse().join('/')}</span>}
                    {r.status === 'fait' && r.resolved_at && <span>Levee le {r.resolved_at.split('-').reverse().join('/')}</span>}
                    {r.client_signed && <span style={{ color: '#16A34A', fontWeight: 700 }}>✔ Signee</span>}
                  </div>
                </div>
                {r.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {r.photos.map((p2, idx) => (
                      <a key={idx} href={`${API_URL}${p2.url}`} target="_blank" rel="noreferrer">
                        <img src={`${API_URL}${p2.url}`} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #E2E2E6' }} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}

        <div style={{ marginTop: 24, padding: '12px 16px', background: 'white', borderRadius: 8, border: '1px solid #E2E2E6', fontSize: '0.82rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={14} color="#002FA7" />
          <span>Portail lecture seule. Pour toute question, contactez votre interlocuteur BATI-BOYS.</span>
        </div>
      </div>
    </div>
  );
};

export default ClientReservationsPortalPage;
